import Graphic from "@arcgis/core/Graphic";
import type { CostType, CareType } from "./LocationBadge";

type LayerInfo = {
  subtitleFieldName?: string;
  locTypeFieldName?: string;
  careTypeFieldName?: string;

  // OPTIONAL: if your layer config has this, use it; otherwise we’ll fall back to common names
  costFieldName?: string;
};

const normalizeLocType = (v?: unknown) => {
  const s = String(v ?? "").trim().toLowerCase();
  if (!s) return undefined;
  if (s.startsWith("center")) return "Center";
  if (s.startsWith("home")) return "Home";
  if (s.startsWith("school")) return "School";
  return undefined;
};

const normalizeCostType = (v?: unknown): CostType | undefined => {
  const s = String(v ?? "").trim().toLowerCase();
  if (!s) return undefined;

  if (s === "free") return "Free";
  if (s.includes("free or small fee") || s.includes("a small fee")) return "Free or a small fee";
  if (s.includes("varies") || s.includes("cost varies")) return "Cost varies, contact provider";

  return undefined;
};


const getCostRawFromGraphic = (loc: Graphic, locationLayerInfo: LayerInfo) => {
  const cfgField = (locationLayerInfo as any)?.costFieldName as string | undefined;
  if (cfgField && loc.attributes?.[cfgField] != null) return loc.attributes[cfgField];

  // common fallbacks — adjust if your field name differs
  return (
    loc.attributes?.COST ??
    loc.getAttribute?.("COST")
  );
};


export function getLocationBadgePropsFromGraphic(loc: Graphic, locationLayerInfo: LayerInfo) {
  const locTypeRaw = locationLayerInfo.locTypeFieldName
    ? loc.attributes?.[locationLayerInfo.locTypeFieldName]
    : undefined;

  const locType = normalizeLocType(locTypeRaw);

  // New rule: use Cost field (preferred)
  const costRaw = getCostRawFromGraphic(loc, locationLayerInfo);
  let costType = normalizeCostType(costRaw);

  // Back-compat fallback (in case cost field missing in some records)
  if (!costType) {
    const subtitleField = locationLayerInfo.subtitleFieldName;
    if (subtitleField && subtitleField.split(";").length === 1) {
      const v = loc.attributes?.[subtitleField];
      if (v && String(v).trim() !== "Private") costType = "Free or a small fee";
    }
  }

  // Care type: use config field if present, else fallback to common field names
  let careType: CareType | undefined = undefined;
  let careTypeRaw: unknown = undefined;
  if (locationLayerInfo.careTypeFieldName) {
    careTypeRaw = loc.attributes?.[locationLayerInfo.careTypeFieldName];
  } else {
    // Fallbacks: try CARETYPE_NAME, CARE_TYPE, CARETYPE
    careTypeRaw = loc.attributes?.["CARETYPE_NAME"]
      ?? loc.attributes?.CARE_TYPE
      ?? loc.attributes?.CARETYPE;
  }
  if (typeof careTypeRaw === "string" && careTypeRaw.trim()) {
    careType = careTypeRaw.trim() as CareType;
  }

  return { locType, costType, careType };
}


export function getLocationBadgePropsFromCardDetails(cardDetails: any, costRaw?: unknown) {
  const locType = normalizeLocType(cardDetails?.locType);
  let costType = normalizeCostType(costRaw);

  // Back-compat fallback for list cards if cost isn’t present yet
  if (!costType) {
    if (cardDetails?.locSubtitle && String(cardDetails.locSubtitle).trim() !== "Private") {
      costType = "Free or a small fee";
    }
  }

  // New: extract careType if present, else fallback to CARETYPE_NAME
  let careType: CareType | undefined = undefined;
  let careTypeRaw: unknown = undefined;
  if (typeof cardDetails?.careType === "string" && cardDetails.careType.trim()) {
    careTypeRaw = cardDetails.careType;
  } else if (typeof cardDetails?.CARETYPE_NAME === "string" && cardDetails.CARETYPE_NAME.trim()) {
    careTypeRaw = cardDetails.CARETYPE_NAME;
  }
  if (typeof careTypeRaw === "string" && careTypeRaw.trim()) {
    careType = careTypeRaw.trim() as CareType;
  }

  return { locType, costType, careType };
}